import { X, LoaderCircle, SearchX, Activity, ChevronRight } from 'lucide-react'
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import { useAppStore } from '../store/app-store'

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={clsx('brand', compact && 'compact')}>
      <span className="brand-mark">
        <img src="./imed-brand-icon.png" alt="iMed" />
      </span>
      {!compact && (
        <span className="brand-name">
          <span style={{ color: '#0277BD', fontWeight: 800 }}>iMed</span>
        </span>
      )}
    </div>
  )
}

export function Button({ className, variant = 'primary', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  return <button className={clsx('button', variant, className)} {...props}>{children}</button>
}

export function Card({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('card', className)} {...props}>{children}</div>
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1>{description && <p>{description}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</header>
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const normalized = Math.max(0, Math.min(100, value))
  return <div className="progress-wrap">{label && <div className="progress-label"><span>{label}</span><b>{Math.round(normalized)}%</b></div>}<div className="progress-track"><span style={{ width: `${normalized}%` }} /></div></div>
}

export function EmptyState({ icon: Icon = SearchX, title, description, action }: { icon?: typeof SearchX; title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><span><Icon /></span><h3>{title}</h3><p>{description}</p>{action}</div>
}

export function LoadingScreen({ inline = false }: { inline?: boolean }) {
  const { pathname } = useLocation()
  const kind = /library|study|books/.test(pathname) ? 'reader' : /quiz|quick-fire/.test(pathname) ? 'quiz' : /videos/.test(pathname) ? 'video' : /auth/.test(pathname) ? 'auth' : 'cards'
  const label = /auth/.test(pathname) ? 'Opening sign in' : /library|study/.test(pathname) ? 'Loading your library' : /books/.test(pathname) ? 'Loading books' : /quiz/.test(pathname) ? 'Preparing your quiz' : /videos/.test(pathname) ? 'Loading lectures' : /flashcards/.test(pathname) ? 'Loading flashcards' : /progress|analytics/.test(pathname) ? 'Loading progress' : 'Opening your workspace'
  return (
    <section className={clsx('imed-wait', inline && 'imed-wait-inline')} role="status" aria-live="polite" aria-label={label}>
      <div className="imed-wait-content">
        <div className="imed-wait-caption"><LoaderCircle aria-hidden="true" /><span>{label}…</span></div>
        <div className={`imed-wait-preview imed-wait-${kind}`} aria-hidden="true">
          <div className="imed-wait-heading" /><div className="imed-wait-line" />
          <div className="imed-wait-blocks">{Array.from({ length: kind === 'quiz' ? 4 : 3 }, (_, i) => <div key={i} className="imed-wait-block"><div /><div /></div>)}</div>
        </div>
      </div>
    </section>
  )
}

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  if (!open) return null
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}><section className="modal" role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Close"><X /></button></header><div className="modal-body">{children}</div>{footer && <footer>{footer}</footer>}</section></div>
}

export function StatCard({ icon: Icon, label, value, detail, tone = 'teal' }: { icon: typeof Activity; label: string; value: string; detail: string; tone?: string }) {
  return <Card className="stat-card"><span className={`stat-icon ${tone}`}><Icon /></span><div><p>{label}</p><h3>{value}</h3><small>{detail}</small></div></Card>
}

export function SectionTitle({ title, action, onAction }: { title: string; action?: string; onAction?: () => void }) {
  return <div className="section-title"><h2>{title}</h2>{action && <button onClick={onAction}>{action}<ChevronRight /></button>}</div>
}

export function ToastViewport() {
  const { toasts, removeToast } = useAppStore()
  return <div className="toast-viewport" aria-live="polite">{toasts.map((toast) => <div className={clsx('toast', toast.tone)} key={toast.id}><div><strong>{toast.title}</strong>{toast.message && <p>{toast.message}</p>}</div><button onClick={() => removeToast(toast.id)} aria-label="Dismiss"><X /></button></div>)}</div>
}
